import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { LegalEntityDetailDto } from '../../features/legal-entity-list/models/legal-entity.models';

// Service to share currently selected legal entity name across components (list -> toolbar)
@Injectable({ providedIn: 'root' })
export class SelectedLegalEntityService {
  // holds the selected legal entity name; empty string means none selected yet
  private _name = signal<string>('');
  // holds the selected legal entity ID; null means none selected yet
  private _id = signal<number | null>(null);
  // Subject to notify when status change is approved/rejected
  private _statusChangeApproved = new Subject<void>();
  // Subject to request entity detail reload
  private _reloadEntityDetail = new Subject<void>();

  // Observable for components to subscribe to status change events
  statusChangeApproved$ = this._statusChangeApproved.asObservable();
  // Observable for components to subscribe to entity detail reload requests
  // Debounced to prevent cascade of multiple simultaneous API calls
  reloadEntityDetail$ = this._reloadEntityDetail.asObservable().pipe(debounceTime(100));
  // holds status code/value from detail endpoint; empty string means unknown/unloaded
  private _status = signal<string>('');
  // holds the bcr_status_id for API calls
  private _bcrStatusId = signal<number | null>(null);
  // loading flag while fetching detail
  private _statusLoading = signal<boolean>(false);
  // loading flags for child component data
  private _ddqFieldsLoading = signal<boolean>(false);
  private _remediationActionsLoading = signal<boolean>(false);
  private _loaDocumentsLoading = signal<boolean>(false);
  // Cache for entity detail to prevent duplicate API calls
  private _entityDetail = signal<LegalEntityDetailDto | null>(null);
  // Active tab ID for legal entity detail view
  private _activeTabId = signal<string>('info');

  name() {
    return this._name();
  }

  setName(value: string) {
    this._name.set(value);
  }

  id() {
    return this._id();
  }

  setId(value: number | null) {
    this._id.set(value);
  }

  status() {
    return this._status();
  }

  setStatus(value: string) {
    this._status.set(value);
  }

  bcrStatusId() {
    return this._bcrStatusId();
  }

  setBcrStatusId(value: number | null) {
    this._bcrStatusId.set(value);
  }

  statusLoading() {
    return this._statusLoading();
  }

  setStatusLoading(value: boolean) {
    this._statusLoading.set(value);
  }

  ddqFieldsLoading() {
    return this._ddqFieldsLoading();
  }

  setDdqFieldsLoading(value: boolean) {
    this._ddqFieldsLoading.set(value);
  }

  remediationActionsLoading() {
    return this._remediationActionsLoading();
  }

  setRemediationActionsLoading(value: boolean) {
    this._remediationActionsLoading.set(value);
  }

  loaDocumentsLoading() {
    return this._loaDocumentsLoading();
  }

  setLoaDocumentsLoading(value: boolean) {
    this._loaDocumentsLoading.set(value);
  }

  entityDetail() {
    return this._entityDetail();
  }

  setEntityDetail(value: LegalEntityDetailDto | null) {
    this._entityDetail.set(value);
  }

  get activeTabId() {
    return this._activeTabId;
  }

  setActiveTabId(value: string) {
    this._activeTabId.set(value);
  }

  clear() {
    this._name.set('');
    this._id.set(null);
    this._status.set('');
    this._bcrStatusId.set(null);
    this._statusLoading.set(false);
    this._ddqFieldsLoading.set(false);
    this._remediationActionsLoading.set(false);
    this._loaDocumentsLoading.set(false);
    this._entityDetail.set(null);
    this._activeTabId.set('info');
  }

  notifyStatusChangeApproved() {
    this._statusChangeApproved.next();
  }

  reloadEntityDetail() {
    this._reloadEntityDetail.next();
  }
}
